import { useMemo, useState } from "react";
import { useDraggable, useDroppable } from "@dnd-kit/core";
import {
  ChevronRight,
  Folder,
  FolderOpen,
  Inbox,
  Pencil,
  Trash2,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { cn } from "@/lib/utils";

/**
 * The folder sidebar.
 *
 * Folders nest, and both folders and recordings can be dragged into them. Two
 * rules keep the interaction honest:
 *
 *  - A folder cannot be dropped into its own subtree. The server rejects it
 *    anyway, but a drop target that accepts and then errors feels broken, so
 *    the invalid targets simply do not light up.
 *  - Dropping is onto a folder, not between them. Ordering folders by hand is a
 *    second concept and this list is alphabetical; adding both at once makes
 *    every drag ambiguous.
 */

export type FolderNode = {
  id: string;
  parentId: string | null;
  name: string;
  recordingCount: number;
};

type TreeNode = FolderNode & { children: TreeNode[]; depth: number };

/** Build the tree from the flat list the API returns. */
function buildTree(folders: FolderNode[]): TreeNode[] {
  const byId = new Map<string, TreeNode>(
    folders.map((f) => [f.id, { ...f, children: [], depth: 0 }]),
  );
  const roots: TreeNode[] = [];

  for (const node of byId.values()) {
    const parent = node.parentId ? byId.get(node.parentId) : undefined;
    if (parent) parent.children.push(node);
    // A folder whose parent is missing (deleted, or outside this tenant) is
    // shown at the root rather than dropped — it still holds recordings.
    else roots.push(node);
  }

  const assignDepth = (nodes: TreeNode[], depth: number) => {
    for (const node of nodes) {
      node.depth = depth;
      node.children.sort((a, b) => a.name.localeCompare(b.name));
      assignDepth(node.children, depth + 1);
    }
  };
  roots.sort((a, b) => a.name.localeCompare(b.name));
  assignDepth(roots, 0);

  return roots;
}

/** Every id inside a subtree, including its own — the invalid drop targets. */
export function subtreeIds(folders: FolderNode[], rootId: string): Set<string> {
  const childrenOf = new Map<string, string[]>();
  for (const folder of folders) {
    if (!folder.parentId) continue;
    const siblings = childrenOf.get(folder.parentId) ?? [];
    siblings.push(folder.id);
    childrenOf.set(folder.parentId, siblings);
  }

  const ids = new Set<string>([rootId]);
  const queue = [rootId];
  while (queue.length > 0) {
    const current = queue.pop();
    if (!current) break;
    for (const child of childrenOf.get(current) ?? []) {
      if (ids.has(child)) continue;
      ids.add(child);
      queue.push(child);
    }
  }
  return ids;
}

export function FolderTree({
  folders,
  selectedId,
  onSelect,
  draggingId,
  onRename,
  onDelete,
}: {
  folders: FolderNode[];
  selectedId: string | null;
  onSelect: (id: string | null) => void;
  /** The folder currently being dragged, so its subtree can refuse drops. */
  draggingId: string | null;
  /** Both endpoints existed with no UI, so a folder was permanent once made. */
  onRename?: (id: string, name: string) => void;
  onDelete?: (id: string) => void;
}) {
  const { t } = useTranslation("library");
  const tree = useMemo(() => buildTree(folders), [folders]);
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());

  const forbidden = useMemo(
    () => (draggingId ? subtreeIds(folders, draggingId) : new Set<string>()),
    [folders, draggingId],
  );

  const toggle = (id: string) => {
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const rows: TreeNode[] = [];
  const flatten = (nodes: TreeNode[]) => {
    for (const node of nodes) {
      rows.push(node);
      if (!collapsed.has(node.id)) flatten(node.children);
    }
  };
  flatten(tree);

  return (
    <nav className="space-y-0.5" aria-label={t("folders.heading")}>
      <RootDrop selected={selectedId === null} onSelect={() => onSelect(null)} />

      {rows.map((node) => (
        <FolderRow
          key={node.id}
          node={node}
          selected={selectedId === node.id}
          collapsed={collapsed.has(node.id)}
          canDrop={!forbidden.has(node.id)}
          onSelect={() => onSelect(node.id)}
          onToggle={() => toggle(node.id)}
          onRename={onRename}
          onDelete={onDelete}
        />
      ))}
    </nav>
  );
}

/** Dropping here files something at the top level. */
function RootDrop({
  selected,
  onSelect,
}: {
  selected: boolean;
  onSelect: () => void;
}) {
  const { t } = useTranslation("library");
  const { setNodeRef, isOver } = useDroppable({ id: "root" });

  return (
    <button
      ref={setNodeRef}
      type="button"
      onClick={onSelect}
      className={cn(
        "flex w-full items-center gap-2 rounded-lg px-2.5 py-1.5 text-left text-[13px] transition-colors",
        selected ? "bg-bg-subtle text-text" : "text-text-muted hover:bg-surface-hover",
        isOver && "ring-1 ring-accent",
      )}
    >
      <Inbox size={14} className="shrink-0" />
      {t("folders.home")}
    </button>
  );
}

function FolderRow({
  node,
  selected,
  collapsed,
  canDrop,
  onSelect,
  onToggle,
  onRename,
  onDelete,
}: {
  node: TreeNode;
  selected: boolean;
  collapsed: boolean;
  canDrop: boolean;
  onSelect: () => void;
  onToggle: () => void;
  onRename?: (id: string, name: string) => void;
  onDelete?: (id: string) => void;
}) {
  const { t } = useTranslation(["library", "common"]);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(node.name);
  const [confirming, setConfirming] = useState(false);
  const { setNodeRef: setDropRef, isOver } = useDroppable({
    id: `folder:${node.id}`,
    disabled: !canDrop,
  });

  const {
    attributes,
    listeners,
    setNodeRef: setDragRef,
    isDragging,
  } = useDraggable({ id: `folder:${node.id}`, data: { type: "folder", id: node.id } });

  const hasChildren = node.children.length > 0;

  return (
    <div
      ref={setDropRef}
      className={cn(
        "rounded-lg transition-colors",
        // Only a valid target lights up. An invalid one stays inert, so the
        // rule is visible before the drop rather than explained after it.
        isOver && canDrop && "bg-accent-subtle ring-1 ring-accent",
        isDragging && "opacity-40",
      )}
      style={{ paddingLeft: node.depth * 14 }}
    >
      <div className="group/folder flex items-center">
        <button
          type="button"
          onClick={onToggle}
          aria-label={collapsed ? t("folders.expand") : t("folders.collapse")}
          className={cn(
            "shrink-0 rounded p-0.5 text-text-faint transition-transform hover:text-text",
            !hasChildren && "invisible",
            !collapsed && "rotate-90",
          )}
        >
          <ChevronRight size={13} />
        </button>

        <button
          ref={setDragRef}
          {...listeners}
          {...attributes}
          type="button"
          onClick={onSelect}
          className={cn(
            "flex min-w-0 flex-1 cursor-grab items-center gap-2 rounded-lg px-1.5 py-1.5",
            "text-left text-[13px] transition-colors active:cursor-grabbing",
            selected ? "bg-bg-subtle text-text" : "text-text-muted hover:bg-surface-hover",
          )}
        >
          {collapsed || !hasChildren ? (
            <Folder size={14} className="shrink-0" />
          ) : (
            <FolderOpen size={14} className="shrink-0" />
          )}
          <span className="truncate">{node.name}</span>
          {node.recordingCount > 0 ? (
            <span className="ml-auto shrink-0 tabular-nums text-[11.5px] text-text-faint">
              {node.recordingCount}
            </span>
          ) : null}
        </button>

        {onRename && onDelete ? (
          <div className="touch-show flex shrink-0 gap-0.5 opacity-0 transition-opacity group-hover/folder:opacity-100 focus-within:opacity-100">
            <button
              type="button"
              aria-label={t("folders.renameNamed", { name: node.name })}
              title={t("common:actions.rename")}
              onClick={() => {
                setDraft(node.name);
                setEditing(true);
              }}
              className="rounded p-1 text-text-faint hover:text-text"
            >
              <Pencil size={11} />
            </button>
            <button
              type="button"
              aria-label={t("folders.deleteNamed", { name: node.name })}
              title={t("common:actions.delete")}
              onClick={() => setConfirming(true)}
              className="rounded p-1 text-text-faint hover:text-danger"
            >
              <Trash2 size={11} />
            </button>
          </div>
        ) : null}
      </div>

      {editing ? (
        <form
          className="flex items-center gap-1 px-1.5 pb-1.5"
          onSubmit={(e) => {
            e.preventDefault();
            const next = draft.trim();
            if (next && next !== node.name) onRename?.(node.id, next);
            setEditing(false);
          }}
        >
          <input
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onBlur={() => setEditing(false)}
            autoFocus
            className="h-7 min-w-0 flex-1 rounded-md border border-accent bg-bg px-2 text-[12.5px] text-text focus:outline-none"
          />
        </form>
      ) : null}

      {confirming ? (
        <div className="px-1.5 pb-1.5">
          <p className="text-[12px] leading-relaxed text-text-muted">
            {/* Worth stating: the tree survives, which is not obvious. */}
            {t("folders.confirmDelete", { name: node.name })}
          </p>
          <div className="mt-1.5 flex gap-1.5">
            <button
              type="button"
              onClick={() => {
                onDelete?.(node.id);
                setConfirming(false);
              }}
              className="rounded-md bg-danger-subtle px-2 py-0.5 text-[11.5px] font-medium text-danger"
            >
              {t("common:actions.delete")}
            </button>
            <button
              type="button"
              onClick={() => setConfirming(false)}
              className="rounded-md px-2 py-0.5 text-[11.5px] text-text-muted hover:text-text"
            >
              {t("common:actions.keep")}
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}
